import { useCallback, useState, type ReactNode, type RefObject } from 'react';
import { alpha, Box, IconButton, type SxProps, type Theme } from '@mui/material';
import type { GridColDef, GridComparatorFn, GridRenderCellParams, GridRowId, GridSortModel, GridValidRowModel } from '@mui/x-data-grid';
import { AddIcon, RemoveIcon } from '../../icons';
import { ROW_EXPAND_COLUMN_PX, ROW_EXPAND_FIELD, ROW_TREE_INDENT_PX } from './styles';

export type HFDataTableRowTreeContext = { depth: number; parentId: GridRowId };

export type HFDataTableRowTree<R extends GridValidRowModel = GridValidRowModel> = {
  /**
   * Children rendered under `row` when it is expanded. A non-empty array makes the row expandable.
   * `undefined` or `[]` hides the toggle (a market row with nothing under it).
   * An allocation with no fills still expands when the caller returns a header plus an empty-state row.
   * Ids must be unique across the whole tree. Children may themselves have children.
   */
  getChildren: (row: R) => readonly R[] | undefined;
  /** Controlled expanded ids. Omit to let the table own them. */
  expandedIds?: ReadonlySet<GridRowId>;
  /** Uncontrolled seed. Read once. */
  defaultExpandedIds?: Iterable<GridRowId>;
  onExpandedIdsChange?: (ids: ReadonlySet<GridRowId>) => void;
  /**
   * When set, each child row (depth ≥ 1) is one full-width cell inside nested panels the table draws.
   * Omit for a same-column tree that reuses `columns`.
   * Section labels, column headers, footers and empty lines are ordinary child rows the caller supplies.
   */
  renderChildRow?: (row: R, ctx: HFDataTableRowTreeContext) => ReactNode;
  /**
   * Depth at which `renderChildRow` takes over. Default 1: every child is a panel.
   * A higher value keeps shallower children in the parent columns and draws deeper children as panels.
   */
  panelFromDepth?: number;
  /** Default `${expanded ? 'Collapse' : 'Expand'} row ${id}`. */
  getToggleLabel?: (row: R, expanded: boolean) => string;
};

/** Depth where panel rows start, or null when children reuse columns. */
export function panelChildDepth<R extends GridValidRowModel>(tree: HFDataTableRowTree<R> | undefined): number | null {
  if (!tree?.renderChildRow) return null;
  const depth = tree.panelFromDepth ?? 1;
  if (!Number.isFinite(depth) || depth < 1) return 1;
  return Math.floor(depth);
}

export function isPanelDepth(panelFrom: number | null, depth: number): boolean {
  return panelFrom != null && depth >= panelFrom;
}

export type RowTreeMeta = {
  depth: number;
  parentId: GridRowId | null;
  hasChildren: boolean;
  /** First child of its parent: this row opens that panel. */
  opensPanel: boolean;
  /** How many nested panels end on this visible row (inner panels first). */
  closesPanels: number;
  /** A sibling at this depth can expand, so leaf rows keep a toggle-sized gap. */
  toggleSlot: boolean;
};

function childList<R extends GridValidRowModel>(getChildren: (row: R) => readonly R[] | undefined, row: R): readonly R[] | undefined {
  const children = getChildren(row);
  if (!children || children.length === 0) return undefined;
  return children;
}

/** Depth-first visible rows. Descend only into expanded parents. */
export function flattenRowTree<R extends GridValidRowModel>(
  parents: readonly R[],
  getChildren: (row: R) => readonly R[] | undefined,
  getId: (row: R) => GridRowId,
  expandedIds: ReadonlySet<GridRowId>,
): { rows: R[]; meta: Map<GridRowId, RowTreeMeta> } {
  const rows: R[] = [];
  const meta = new Map<GridRowId, RowTreeMeta>();

  const walk = (list: readonly R[], depth: number, parentId: GridRowId | null) => {
    const prepared = list.map((row) => {
      const children = childList(getChildren, row);
      return { row, children, hasChildren: children != null };
    });
    const toggleSlot = depth > 0 && prepared.some((item) => item.hasChildren);

    for (let index = 0; index < prepared.length; index += 1) {
      const item = prepared[index];
      if (!item) continue;
      const id = getId(item.row);
      meta.set(id, {
        depth,
        parentId,
        hasChildren: item.hasChildren,
        opensPanel: depth > 0 && index === 0,
        closesPanels: 0,
        toggleSlot,
      });
      rows.push(item.row);
      if (item.hasChildren && item.children && expandedIds.has(id)) {
        const before = rows.length;
        walk(item.children, depth + 1, id);
        if (rows.length > before) {
          const lastId = getId(rows[rows.length - 1] as R);
          const last = meta.get(lastId);
          if (last) last.closesPanels += 1;
        }
      }
    }
  };

  walk(parents, 0, null);
  return { rows, meta };
}

/** Deepest depth that has an expand toggle, walking the whole tree (not only visible rows). Depths past `maxDepth` are ignored. */
export function toggleColumnDepth<R extends GridValidRowModel>(
  parents: readonly R[],
  getChildren: (row: R) => readonly R[] | undefined,
  maxDepth = Infinity,
): number {
  let max = 0;
  const walk = (list: readonly R[], depth: number) => {
    if (depth > maxDepth) return;
    for (const row of list) {
      const children = childList(getChildren, row);
      if (!children) continue;
      max = Math.max(max, depth);
      walk(children, depth + 1);
    }
  };
  walk(parents, 0);
  return max;
}

function readSortValue<R extends GridValidRowModel>(col: GridColDef<R>, row: R, apiRef: RefObject<unknown> | undefined): unknown {
  const raw = (row as Record<string, unknown>)[col.field];
  if (typeof col.valueGetter === 'function') {
    return col.valueGetter(raw as never, row, col, apiRef as never);
  }
  return raw;
}

function defaultCompare(a: unknown, b: unknown, type: string | undefined): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (type === 'number') return Number(a) - Number(b);
  if (type === 'date' || type === 'dateTime') {
    const at = a instanceof Date ? a.getTime() : new Date(a as string | number).getTime();
    const bt = b instanceof Date ? b.getTime() : new Date(b as string | number).getTime();
    return at - bt;
  }
  return sortableText(a).localeCompare(sortableText(b), undefined, { numeric: true, sensitivity: 'base' });
}

function sortableText(value: unknown): string {
  switch (typeof value) {
    case 'string':
    case 'number':
    case 'boolean':
    case 'bigint':
      return String(value);
    default:
      return '';
  }
}

/** Sort parent rows only. Community is single-column. Stable when values tie. */
export function sortRowsByModel<R extends GridValidRowModel>(
  rows: readonly R[],
  sortModel: GridSortModel,
  columns: Array<GridColDef<R>>,
  apiRef?: RefObject<unknown>,
  getId: (row: R) => GridRowId = (row) => (row as { id?: GridRowId }).id ?? '',
): R[] {
  const item = sortModel.find((entry) => entry.sort);
  if (!item?.sort) return [...rows];
  const col = columns.find((column) => column.field === item.field);
  if (!col) return [...rows];

  const direction = item.sort;
  let comparator: GridComparatorFn | undefined;
  if (col.getSortComparator) {
    comparator = col.getSortComparator(direction);
  } else if (col.sortComparator) {
    const base = col.sortComparator;
    comparator = direction === 'desc' ? (v1, v2, p1, p2) => -1 * base(v1, v2, p1, p2) : base;
  }

  const decorated = rows.map((row, index) => ({ row, index, id: getId(row), value: readSortValue(col, row, apiRef) }));
  decorated.sort((a, b) => {
    const result = comparator
      ? comparator(
          a.value,
          b.value,
          { id: a.id, field: col.field, value: a.value, rowNode: { id: a.id } as never, api: apiRef?.current },
          { id: b.id, field: col.field, value: b.value, rowNode: { id: b.id } as never, api: apiRef?.current },
        )
      : defaultCompare(a.value, b.value, col.type) * (direction === 'desc' ? -1 : 1);
    if (result === 0) return a.index - b.index;
    return result;
  });
  return decorated.map((entry) => entry.row);
}

export function useRowTreeExpanded<R extends GridValidRowModel>(
  tree: HFDataTableRowTree<R> | undefined,
): {
  expandedIds: ReadonlySet<GridRowId>;
  toggle: (id: GridRowId) => void;
} {
  const [uncontrolled, setUncontrolled] = useState<ReadonlySet<GridRowId>>(() => new Set(tree?.defaultExpandedIds ?? []));
  const controlled = tree?.expandedIds;
  const onChange = tree?.onExpandedIdsChange;
  const expandedIds = controlled ?? uncontrolled;

  const toggle = useCallback(
    (id: GridRowId) => {
      const current = controlled ?? uncontrolled;
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onChange?.(next);
      if (controlled === undefined) setUncontrolled(next);
    },
    [controlled, uncontrolled, onChange],
  );

  return { expandedIds, toggle };
}

function rowExpandButton(expanded: boolean, label: string, tabIndex: number, onToggle: () => void) {
  return (
    <IconButton
      size="small"
      aria-expanded={expanded}
      aria-label={label}
      tabIndex={tabIndex}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Enter') event.stopPropagation();
      }}
      sx={{
        width: 22,
        height: 22,
        p: 0,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 1,
        borderColor: 'divider',
        borderRadius: '6px',
        color: 'text.primary',
        '& .MuiSvgIcon-root': { display: 'block' },
      }}
    >
      {expanded ? <RemoveIcon sx={{ fontSize: 16 }} /> : <AddIcon sx={{ fontSize: 16 }} />}
    </IconButton>
  );
}

/** Panel content sits in the row. Ancestor levels only indent; they do not draw a second card. */
function panelFrames(depth: number, content: ReactNode, panelFrom: number): ReactNode {
  let node = content;
  for (let level = depth; level > panelFrom; level -= 1) {
    node = <Box sx={{ display: 'flex', flex: 1, minWidth: 0, pl: 2 }}>{node}</Box>;
  }
  return node;
}

export function rowTreeColumn<R extends GridValidRowModel>(options: {
  meta: Map<GridRowId, RowTreeMeta>;
  expandedIds: ReadonlySet<GridRowId>;
  toggle: (id: GridRowId) => void;
  tree: HFDataTableRowTree<R>;
  spanCount: number;
  width: number;
  getId: (row: R) => GridRowId;
}): GridColDef<R> {
  const { meta, expandedIds, toggle, tree, spanCount, width, getId } = options;
  const panelFrom = panelChildDepth(tree);

  return {
    field: ROW_EXPAND_FIELD,
    headerName: '',
    width,
    minWidth: width,
    maxWidth: width,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    resizable: false,
    colSpan: (_value, row) => {
      const depth = meta.get(getId(row))?.depth ?? 0;
      return isPanelDepth(panelFrom, depth) ? spanCount : 1;
    },
    cellClassName: (params) => {
      const depth = meta.get(params.id)?.depth ?? 0;
      return isPanelDepth(panelFrom, depth) ? 'hf-row-span' : '';
    },
    renderCell: (params: GridRenderCellParams<R>) => {
      const rowMeta = meta.get(params.id);
      if (!rowMeta) return null;
      const expanded = expandedIds.has(params.id);
      const label = tree.getToggleLabel?.(params.row, expanded) ?? `${expanded ? 'Collapse' : 'Expand'} row ${params.id}`;
      const toggleButton = rowMeta.hasChildren ? (
        rowExpandButton(expanded, label, params.tabIndex, () => toggle(params.id))
      ) : rowMeta.toggleSlot ? (
        <Box sx={{ width: 22, height: 22, flexShrink: 0 }} />
      ) : null;

      if (isPanelDepth(panelFrom, rowMeta.depth) && tree.renderChildRow) {
        const content = (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, width: '100%', minWidth: 0 }}>
            {toggleButton}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {tree.renderChildRow(params.row, { depth: rowMeta.depth, parentId: rowMeta.parentId ?? params.id })}
            </Box>
          </Box>
        );
        return (
          <Box sx={{ display: 'flex', alignItems: 'stretch', width: '100%', height: '100%', minWidth: 0 }}>
            {panelFrames(rowMeta.depth, content, panelFrom ?? 1)}
          </Box>
        );
      }

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            pl: panelFrom === 1 ? 0 : (rowMeta.depth * ROW_TREE_INDENT_PX) / 8,
          }}
        >
          {toggleButton}
        </Box>
      );
    },
  };
}

/** Same-column indent, the open-parent wash, and the child-row marker. Panel mode passes `indentDepth` 0. */
export function rowTreeSx(firstDataField: string | undefined, indentDepth: number): SxProps<Theme> {
  const sx: Record<string, object> = {
    '& .MuiDataGrid-row.hf-row-expanded': {
      '--hf-row-bg': (theme: Theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.06),
      bgcolor: 'var(--hf-row-bg)',
    },
    // A child sits under its parent: stronger wash plus a left bar. Later than the
    // expanded rule so an open nested parent still reads as a child.
    '& .MuiDataGrid-row.hf-row-child': {
      '--hf-row-bg': (theme: Theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.12),
      bgcolor: 'var(--hf-row-bg)',
      '& > .MuiDataGrid-cell:first-of-type': {
        boxShadow: (theme: Theme) => `inset 3px 0 0 ${theme.palette.primary.main}`,
      },
      '&:hover': {
        '--hf-row-bg': (theme: Theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.18),
        bgcolor: 'var(--hf-row-bg)',
      },
    },
    // A full-width detail panel continues the open parent. No second wash, bar, or card.
    '& .MuiDataGrid-row.hf-row-child:has(.hf-row-span)': {
      '--hf-row-bg': (theme: Theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.04),
      bgcolor: 'var(--hf-row-bg)',
      '& > .MuiDataGrid-cell:first-of-type': { boxShadow: 'none' },
      '&:hover': {
        '--hf-row-bg': (theme: Theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.07),
        bgcolor: 'var(--hf-row-bg)',
      },
    },
    '& .hf-row-child .MuiDataGrid-cellCheckbox > *': { visibility: 'hidden' },
    '& .MuiDataGrid-cell.hf-row-span': {
      p: 0,
      overflow: 'visible',
      '&.MuiDataGrid-cell': { alignItems: 'stretch' },
    },
    [`& .MuiDataGrid-cell[data-field="${ROW_EXPAND_FIELD}"]`]: {
      display: 'flex',
      alignItems: 'center',
      px: 0.5,
      lineHeight: 1,
    },
  };

  if (firstDataField) {
    for (let depth = 1; depth <= indentDepth; depth += 1) {
      sx[`& .hf-row-depth-${depth} .MuiDataGrid-cell[data-field="${firstDataField}"]`] = {
        pl: `${10 + depth * ROW_TREE_INDENT_PX}px`,
      };
    }
  }

  return sx;
}

export function expandColumnWidth(panel: boolean, depth: number): number {
  if (panel) return ROW_EXPAND_COLUMN_PX;
  return ROW_EXPAND_COLUMN_PX + ROW_TREE_INDENT_PX * depth;
}
